import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import type {DateRange} from "../../common/newDatePicker/datePickerShared";
import CrmGuestValueSections from "./CrmGuestValueSections";
import {
    AgeCategoriesCard,
    AgeDistributionCard,
    GenderSplitCard,
    PartySizeCard,
} from "./crm-demographics/DemographicCards";
import DemographicsStatCards, {DEMOGRAPHICS_STAT_METRICS} from "./crm-demographics/DemographicsStatCards";
import {insightsLoadingOf} from "../kit/insightsLoading";
import {
    BirthdayMonthsCard,
    BookingActivityCard,
    CountriesCard,
    LanguagesCard,
    ReservationAccountTypeCard,
    RevenueByCountryCard,
    TopCitiesCard,
    TravelDistanceCard,
} from "./crm-demographics/GuestProfileCards";
import InsightGuestMapCard from "./map/InsightGuestMapCard";
import {
    useInsightsV2DemographicCharts,
    useInsightsV2DemographicsStats,
} from "./useInsightsV2Stats";

const DEMOGRAPHICS_STAT_TITLES = DEMOGRAPHICS_STAT_METRICS.map(definition => definition.title);

const DEMOGRAPHIC_CARD_TITLES = ["Age categories", "Party size", "Age distribution", "Gender split"];
const GUEST_PROFILE_CARD_TITLES = [
    "Languages used",
    "Reservation account type",
    "Booking activity",
    "Birthday months",
    "Revenue by country",
    "Countries",
    "Top cities",
    "Travel distance",
];

interface CrmDemographicsTabProps {
    range: DateRange;
    compareRange: DateRange | null;
    /** The rail's search. A section whose every card is filtered out drops its heading with them. */
    matches?: (title: string, key: string) => boolean;
}

/**
 * Who the guests are: age mix, party size, origin, and how many of them had stayed before. The range and the
 * targets dialog belong to the surrounding layout, so this owns only the section's body.
 */
export function CrmDemographicsTab({range, compareRange, matches}: CrmDemographicsTabProps) {
    const {t} = useTranslation();
    const statsQuery = useInsightsV2DemographicsStats(range, compareRange);
    const chartsQuery = useInsightsV2DemographicCharts(range, compareRange);
    const statsLoading = insightsLoadingOf(
        statsQuery.isLoading,
        compareRange != null && statsQuery.isPreviousData,
    );
    const chartsLoading = insightsLoadingOf(
        chartsQuery.isLoading,
        compareRange != null && chartsQuery.isPreviousData,
    );
    /*
     * First load only. On `isFetching` every background refetch unmounted the map and mounted a new
     * one, so a range change cost a full mapbox start-up - the previous period's dots are a better
     * thing to look at while the next ones arrive.
     */
    const mapLoading = chartsQuery.isLoading;

    const shows = (title: string, key: string = title) => matches == null || matches(t(title), key);
    const showStats = DEMOGRAPHICS_STAT_TITLES.some(title => shows(title));
    const showMap = shows("Guest origins");
    const demographicCards = DEMOGRAPHIC_CARD_TITLES.filter(title => shows(title));
    const profileCards = GUEST_PROFILE_CARD_TITLES.filter(title => shows(title));

    if (!showStats && !showMap && demographicCards.length === 0 && profileCards.length === 0) {
        return (
            <Typography variant={"body2"} color={"text.secondary"} sx={{py: 6, textAlign: "center"}}>
                {t("No insights match your search.")}
            </Typography>
        );
    }

    const showsCard = (title: string) => demographicCards.includes(title) || profileCards.includes(title);

    return (
        <Stack gap={3}>
            {showStats && (
            <Stack gap={1}>
                <Typography variant="body1" color="text.secondary">
                    {t("All metrics")}
                </Typography>

                <DemographicsStatCards
                    tiles={statsQuery.data}
                    loading={statsLoading}
                    comparing={compareRange != null}
                    matches={matches}
                />
            </Stack>
            )}

            {showMap && (
            <Stack gap={1}>
                <Typography variant="body1" color="text.secondary">
                    {t("Geography")}
                </Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "minmax(0, 1fr)", xl: "repeat(2, minmax(0, 1fr))"},
                        gap: 2,
                        minWidth: 0,
                    }}
                >
                    <InsightGuestMapCard
                        map={chartsQuery.data?.guestMap}
                        loading={mapLoading}
                        comparing={compareRange != null}
                    />
                </Box>
            </Stack>
            )}

            <CrmGuestValueSections range={range} compareRange={compareRange} matches={matches}/>

            {demographicCards.length > 0 && (
            <Stack gap={1}>
                <Typography variant="body1" color="text.secondary">
                    {t("Demographics")}
                </Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "minmax(0, 1fr)", xl: "repeat(2, minmax(0, 1fr))"},
                        gap: 2,
                        minWidth: 0,
                    }}
                >
                    {showsCard("Party size") && (
                    <PartySizeCard
                        items={chartsQuery.data?.partySizeDistribution ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                    {showsCard("Age categories") && (
                    <AgeCategoriesCard
                        items={chartsQuery.data?.ageCategorySplit ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                    {showsCard("Age distribution") && (
                    <AgeDistributionCard
                        items={chartsQuery.data?.ageDistribution ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                    {showsCard("Gender split") && (
                    <GenderSplitCard
                        items={chartsQuery.data?.genderSplit ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                </Box>
            </Stack>
            )}

            {profileCards.length > 0 && (
            <Stack gap={1}>
                <Typography variant="body1" color="text.secondary">
                    {t("Guest profile")}
                </Typography>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "minmax(0, 1fr)", xl: "repeat(2, minmax(0, 1fr))"},
                        gap: 2,
                        minWidth: 0,
                    }}
                >
                    {showsCard("Languages used") && (
                    <LanguagesCard
                        items={chartsQuery.data?.languages ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                    {showsCard("Reservation account type") && (
                    <ReservationAccountTypeCard
                        items={chartsQuery.data?.accountTypes ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                    {showsCard("Booking activity") && (
                    <BookingActivityCard
                        items={chartsQuery.data?.bookingActivity ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                    {showsCard("Birthday months") && (
                    <BirthdayMonthsCard
                        items={chartsQuery.data?.birthdayMonths ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                    {showsCard("Revenue by country") && (
                    <RevenueByCountryCard
                        items={chartsQuery.data?.revenueByCountry ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                    {showsCard("Countries") && (
                    <CountriesCard
                        items={chartsQuery.data?.countries ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                    {showsCard("Top cities") && (
                    <TopCitiesCard
                        items={chartsQuery.data?.cities ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                    {showsCard("Travel distance") && (
                    <TravelDistanceCard
                        items={chartsQuery.data?.travelDistance ?? []}
                        loading={chartsLoading}
                        comparing={Boolean(compareRange)}
                    />
                    )}
                </Box>
            </Stack>
            )}
        </Stack>
    );
}

export default CrmDemographicsTab;
